import * as _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { IInvoiceTransplants } from '@nkr/models/index';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, FirstDataRenderedEvent, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { Button, Glyphicon, Label } from 'react-bootstrap';
import CheckboxRendererInvoice from '@nkr/components/checkbox-renderer-invoice';
import OverlayMenuButton, { IMenuItem } from '@nkr/components/overlay-menu-button';
import * as moment from "moment";

import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-bootstrap.css';
import { GridWrapper } from './styles';

enum InvoiceAction {
    View,
    Regenerate,
    AuditDonorsProtected,
    Preview
}

interface IProps {
    list: IInvoiceTransplants[],
    isAdmin: boolean,
    isAdminLogin: boolean,
    isLoading: boolean,
    onSelectAllRows: () => void,
    onRowSelected: (id: number, isSelected: boolean, centerId: number, organType: number) => void,
    onLoadingComplete: () => void,
    onGridReady: (e: GridReadyEvent) => void
    onPreviewClicked: (centerId: number, invoiceDate: string, organType: number) => void
    onViewClicked: (invoiceNumber: number) => void
    onRegenerateClicked: (invoiceNumber: number, organType: number) => void
}

const Grid = (props: IProps) => {
    const [menuDisplayed, setMenuDisplayed] = useState(false);

    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();
        props.onGridReady(e);
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
        props.onLoadingComplete();
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onBodyScroll = () => {
        if (menuDisplayed) {
            document.body.click();
            setMenuDisplayed(false);
        }
    };

    const handleRowSelected = (id: number, isSelected: boolean, centerId: number, organType: number) => {
        props.onRowSelected(id, isSelected, centerId, organType);
    };

    const handlePreview = (centerId: number, invoiceDate: string, organType: number) => {
        props.onPreviewClicked(centerId, invoiceDate, organType);
    };

    const handleView = (invoiceNumber: number) => {
        props.onViewClicked(invoiceNumber);
    };

    const handleRegenerate = (invoiceNumber: number, organType: number) => {
        props.onRegenerateClicked(invoiceNumber, organType);
    };

    const handleMenuToggle = (show: boolean) => {
        setMenuDisplayed(show);
    };

    const handleMenuSelection = (data: IInvoiceTransplants, action: InvoiceAction) => {
        switch (action) {
            case InvoiceAction.View: handleView(data.invoiceNumber); break;
            case InvoiceAction.Regenerate: handleRegenerate(data.invoiceNumber, data.organType); break;
            case InvoiceAction.Preview: handlePreview(data.centerId, data.invoiceDate, data.organType); break;
        }
    };

    const handleSelectAllRows = () => {
        props.onSelectAllRows();
    };

    const onSortChanged = (e: any) => {
        e.api.refreshCells({ columns: ['rank'], force: true });
    };

    const getTotals = (data: IInvoiceTransplants[]): IInvoiceTransplants => {
        let totalTxps = _.sumBy(data, item => item.transplants);
        let totalFacKTxps = _.sumBy(data, item => item.transplantsFacilitatedK);
        let totalNonFacKTxps = _.sumBy(data, item => item.transplantsNonFacilitatedK);
        let totalFacLTxps = _.sumBy(data, item => item.transplantsFacilitatedL);
        let totalNonFacLTxps = _.sumBy(data, item => item.transplantsNonFacilitatedL);

        let totalInVoiceAmount = _.sumBy(data, item => item.invoiceAmount);

        return {
            id: 0,
            centerId: 0,
            centerAlias: 'TOTALS:',
            organType: 0,
            invoiceNumber: 0,
            lastUpdated: '',
            invoiceSent: '',
            protectedReportEndDate: '',
            received: '',
            transplants: totalTxps,
            transplantsFacilitatedK: totalFacKTxps,
            transplantsNonFacilitatedK: totalNonFacKTxps,
            transplantsFacilitatedL: totalFacLTxps,
            transplantsNonFacilitatedL: totalNonFacLTxps,
            invoiceAmount: totalInVoiceAmount,
            missingContact: false,
            isSelected: false,
            invoiceDate: '',
            missingFees: false,
            isBillableKidney: false,
            isBillableLiver: false
        };
    }

    const cols: ColDef[] = [
        {
            colId: 'isSelected',
            field: 'isSelected',
            headerName: '',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}><Glyphicon glyph='check' title='Check/Uncheck All Rows' onClick={handleSelectAllRows} /></SortableHeaderComponent>,
            width: 35,
            sortable: false,
            suppressSizeToFit: true,
            cellRendererParams: { onValueChange: handleRowSelected },
            cellRenderer: 'checkboxRenderer'
        },
        {
            colId: 'rank',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
            headerName: 'R#',
            width: 40,
            cellClass: 'list-cell',
            headerClass: 'list-header',
            valueGetter: (params: ValueGetterParams) => {
                if (params.node.rowPinned) return '';
                let index = 0;
                params.api.forEachNodeAfterFilterAndSort((node, i) => {
                    if (node.id === params.node.id) index = i + 1;
                });
                return index;
            },
            sortable: false
        },
        {
            colId: 'actions',
            field: '',
            headerName: 'actions',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Actions</SortableHeaderComponent>,
            sortable: false,
            cellStyle: { overflow: 'visible' },
            width: 120,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IInvoiceTransplants;

                if (!(data.centerId > 0)) {
                    return <></>;
                }

                const hasInvoice = data.invoiceNumber > 0;
                const showAudit = hasInvoice && moment(data.protectedReportEndDate).isValid();

                const menu: IMenuItem[] = [
                    { id: data.centerId, action: InvoiceAction.View, include: hasInvoice, selectable: true, element: <>View Invoice</> },
                    { id: data.centerId, action: InvoiceAction.Regenerate, include: hasInvoice, selectable: true, element: <>Regenerate Invoice</> },
                    { id: `divider-invoice-${data.centerId}`, action: -1, include: hasInvoice, selectable: false, divider: true, element: null },
                    { id: data.centerId, action: InvoiceAction.AuditDonorsProtected, include: showAudit, selectable: false, element: <a href={`/report/donors-protected?ctrID=${data.centerId}&endDate=${data.protectedReportEndDate}`} target='_blank'>Audit Donors Protected</a> },
                    { id: `divider-audit-${data.centerId}`, action: -1, include: showAudit, selectable: false, divider: true, element: null },
                    { id: data.centerId, action: InvoiceAction.Preview, include: true, selectable: true, element: <>Generate Preview (non-saving)</> }
                ];

                return (
                    <OverlayMenuButton
                        menu={menu}
                        bsStyle='primary'
                        bsSize='xs'
                        title='Actions'
                        includeCaret={true}
                        listItemStyle={{ padding: '3px 10px', fontSize: 14 }}
                        onToggle={handleMenuToggle}
                        onSelect={(id, action) => handleMenuSelection(data, action)}
                    />
                );
            }
        },
        {
            colId: 'organType',
            field: 'organType',
            headerName: 'Organ Type',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>Organ Type</SortableHeaderComponent>,
            width: 150,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IInvoiceTransplants;
                if (params.node.rowPinned) return '';
                let organType = data.organType == null ? '--' : data.organType == 1 ? 'L' : 'K';
                return <Label bsStyle={data.organType == null ? 'default' : data.organType == 1 ? 'warning' : 'danger'} style={{ fontSize: '100%' }}>{organType}</Label>;
            }
        },
        {
            colId: 'id',
            field: 'id',
            headerName: 'Id',
            sortable: false,
            width: 120,
            hide: false
        },
        {
            colId: 'centerAlias',
            field: 'centerAlias',
            headerName: 'Center',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
            width: 120,
        },
        {
            colId: 'invoiceNumber',
            field: 'invoiceNumber',
            headerName: 'Invoice #',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Invoice #</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IInvoiceTransplants;

                if (data.id == 0) {
                    return '';
                }

                return params.value;
            },
            width: 100,
        },
        {
            colId: 'lastUpdated',
            field: 'lastUpdated',
            headerName: 'Last Updated',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Last Updated</SortableHeaderComponent>,
            width: 200,
        },
        {
            colId: 'invoiceSent',
            field: 'invoiceSent',
            headerName: 'Invoice Sent',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Invoice Sent</SortableHeaderComponent>,
            width: 200,
        },
        {
            colId: 'transplantsFacilitatedK',
            field: 'transplantsFacilitatedK',
            headerName: 'TXPs Facilitated Kidney',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>TXPs Facilitated<br />Kidney</SortableHeaderComponent>,
            width: 200,
        },
        {
            colId: 'transplantsNonFacilitatedK',
            field: 'transplantsNonFacilitatedK',
            headerName: 'TXPs Supported Kidney',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>TXPs Supported<br />Kidney</SortableHeaderComponent>,
            width: 200,
        },
        {
            colId: 'transplantsFacilitatedL',
            field: 'transplantsFacilitatedL',
            headerName: 'TXPs Facilitated Liver',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>TXPs Facilitated<br />Liver</SortableHeaderComponent>,
            width: 200,
        },
        {
            colId: 'transplantsNonFacilitatedL',
            field: 'transplantsNonFacilitatedL',
            headerName: 'TXPs Supported Liver',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>TXPs Supported<br />Liver</SortableHeaderComponent>,
            width: 200,
        },
        {
            colId: 'invoiceAmount',
            field: 'invoiceAmount',
            headerName: 'Invoice Amount',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Invoice Amount</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                return <>{props.data.invoiceAmount != '' ? <span style={{ textAlign: 'left' }}> ${props.data.invoiceAmount.toLocaleString('en-us', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span> : '--'}</>;
            },
            width: 200
        },
        {
            colId: 'missingFees',
            field: 'missingFees',
            headerName: 'Missing Fees',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Missing Fees</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IInvoiceTransplants;

                if (data.missingFees) {
                    return <Glyphicon style={{ color: 'red' }} glyph='flag' />;
                } else {
                    return '';
                }
            },
            width: 200,
        },
        {
            colId: 'missingContact',
            field: 'missingContact',
            headerName: 'Missing Contact',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Missing Contact</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IInvoiceTransplants;

                if (data.missingContact) {
                    return <Glyphicon style={{ color: 'red' }} glyph='flag' />;
                } else {
                    return '';
                }
            },
            width: 200,
        }
    ];

    const columnDefs = useMemo(() => cols, []);
    const list = props.list.length > 0 ? props.list : (props.isLoading ? null : []);
    const rowData = useMemo(() => list, [list]);
    const totals = getTotals(rowData);

    return (
        <GridWrapper id='grid-wrapper' className='container-fluid-max-sm ag-theme-bootstrap'>
            <AgGridReact
                defaultColDef={{
                    sortable: true
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                immutableData={true}
                getRowNodeId={(data: IInvoiceTransplants) => data.id != null ? `invoice-${data.id}` : `center-${data.centerId}-${data.organType}`}
                headerHeight={38}
                rowHeight={30}
                suppressCellSelection={true}
                suppressContextMenu={true}
                overlayNoRowsTemplate={`No invoice records...`}
                overlayLoadingTemplate={`Loading invoice records...`}
                frameworkComponents={{
                    checkboxRenderer: CheckboxRendererInvoice
                }}
                onGridReady={onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onBodyScroll={onBodyScroll}
                onGridSizeChanged={onGridSizeChanged}
                onRowDataChanged={onRowDataChanged}
                onSortChanged={onSortChanged}
                pinnedBottomRowData={[totals]}
                columnHoverHighlight={true}
                rowStyle={{ overflow: 'visible' }}
            />
        </GridWrapper>
    );
}

export default Grid;